// Sends console.warn/error, uncaught errors and unhandled rejections from the login app to OTel (Loki)

import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { userAttributes } from "./instrumentation";
import { getResource } from './resource';
import type * as logsAPI from '@opentelemetry/api-logs';

type Level = 'warn' | 'error';
type Source = 'browser-console' | 'window-error' | 'unhandled-rejection';

// URL/path of a stack frame in V8 ("at fn (url:1:2)", "at url:1:2") or Firefox/Safari ("fn@url:1:2") format
const FRAME_LOCATION = /(?:\(|@|\bat\s)([^()\s@]+):\d+:\d+\)?$/;
const EXTENSION_URL = /^(?:chrome|moz|safari-web)-extension:\/\//;
// A script served from another site (RudderStack, Firebase, Clarity, Google): its errors are recorded but are not login bugs
const isThirdPartyUrl = (url: string): boolean => /^https?:\/\//.test(url) && !url.startsWith(`${window.location.origin}/`);
const frameUrl = (frame: string): string => frame.match(FRAME_LOCATION)?.[1] ?? '';
// Stack frames only, without Chrome's "Error: message" header line
const stackFrames = (stack?: string): string[] =>
  (stack ?? '').split('\n').map(line => line.trim()).filter(line => line.startsWith('at ') || FRAME_LOCATION.test(line));
// The frame that actually called console.*: when something wrapped console.* after us (e.g. React DevTools), its own frames come first, so skip them
// Deeper frames can't decide this: zone.js wraps every timer/promise, so even extension code runs inside frames from our bundle
const directCaller = (frames: string[], wrappedAfterUs: boolean): string => {
  let i = 0;
  if (wrappedAfterUs && frames.length > 0) {
    const wrapperUrl = frameUrl(frames[0]);
    while (i < frames.length && frameUrl(frames[i]) === wrapperUrl) i++;
  }
  return frames[i] ?? frames[0] ?? 'unknown location';
};

// A login token works as a password, so it is never sent; its claims (expiry, age, login source, session) are what the team debugs with
const JWT = /eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g;
const BEARER_OPAQUE = /(Bearer\s+)(?!jwt\()[A-Za-z0-9\-._~+/]+=*/gi;
const TOKEN_PARAM = /((?:access_token|refresh_token|sso_auth_token|token)=)[^&\s"']+/gi;
const decodeJwtPayload = (jwt: string): Record<string, unknown> | null => {
  try {
    const part = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(part.padEnd(part.length + ((4 - (part.length % 4)) % 4), '=')));
  } catch {
    return null;
  }
};
const describeToken = (jwt: string): string => {
  const claims = decodeJwtPayload(jwt);
  if (!claims) return 'jwt(***)';
  const { client_code, source, applicationId, token_type, iat, exp, uuid } = claims;
  const at = (seconds: unknown) => (typeof seconds === 'number' ? new Date(seconds * 1000).toISOString() : null);
  const expiry = at(exp) && `exp ${at(exp)} ${(exp as number) * 1000 < Date.now() ? 'EXPIRED' : 'valid'}`;
  const parts = [client_code && `client ${client_code}`, source && `source ${source}`, applicationId && `app ${applicationId}`,
    token_type && `type ${token_type}`, at(iat) && `issued ${at(iat)}`, expiry, uuid && `session ${uuid}`];
  return `jwt(${parts.filter(Boolean).join(', ')})`;
};
const maskTokens = (text: string): string =>
  text.replace(JWT, describeToken).replace(BEARER_OPAQUE, '$1***').replace(TOKEN_PARAM, '$1***');

// The Authorization header a failed axios request carried, so a 401 still says which token (masked) it was sent with
const authHeaderOf = (error: Error): string => {
  const headers = (error as { config?: { headers?: Record<string, unknown> & { get?: (name: string) => unknown } } }).config?.headers;
  const value = typeof headers?.get === 'function' ? headers.get('Authorization') : headers?.Authorization ?? headers?.authorization;
  return typeof value === 'string' ? value : '';
};
// JSON.stringify(Error) is "{}" (name, message and stack are not enumerable) and it throws on circular objects
const describeError = (error: Error): string => {
  const auth = authHeaderOf(error);
  return `${error.name}: ${error.message}${auth ? ` [auth ${auth}]` : ''}`;
};
const serializeArg = (arg: unknown): unknown => {
  if (arg instanceof Error) return describeError(arg);
  if (typeof arg !== 'object') return arg;
  try {
    // An Error nested in an object ({ error: err }) gets the same name: message; checks the original value because toJSON (e.g. AxiosError's, which includes request headers) runs before the replacer
    return JSON.stringify(arg, function (this: Record<string, unknown>, key: string, value: unknown) {
      const original = this[key];
      return original instanceof Error ? describeError(original) : value;
    });
  } catch {
    return String(arg);
  }
};

// A request that never reached a server (user offline / flaky network) or timed out: not a login bug, so warn not error
const NETWORK_MESSAGE = /^(?:Failed to fetch|NetworkError when attempting to fetch resource\.?|Load failed|Network Error)$/i;
// Only a request that timed out (axios's own message); app errors that merely mention a timeout ("Session timed out") stay errors
const TIMEOUT_MESSAGE = /^timeout of \d+ms exceeded$/i;
const networkCategory = (error?: Error): 'network' | 'timeout' | null => {
  if (!error) return null;
  const code = (error as { code?: string }).code;
  if (code === 'ERR_NETWORK' || NETWORK_MESSAGE.test(error.message)) return 'network';
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT' || error.name === 'TimeoutError' || TIMEOUT_MESSAGE.test(error.message)) return 'timeout';
  return null;
};

let logger: logsAPI.Logger | undefined;
// Set while emitting, so a console call made from inside the logger can't recurse into it
let inLog = false;
// zone.js reports an unhandled rejection through console.error and the browser may also fire unhandledrejection: send each Error once
const reported = new WeakSet<Error>();

type Category = 'app' | 'network' | 'timeout' | 'third_party';
const emitLog = (level: Level, source: Source, args: unknown[], stack: string, location: string, thirdParty = false): void => {
  if (!logger || inLog) return;
  inLog = true;
  try {
    const firstError = args.find((arg): arg is Error => arg instanceof Error);
    if (firstError) {
      if (reported.has(firstError)) return;
      reported.add(firstError);
    }
    const category: Category = thirdParty ? 'third_party' : networkCategory(firstError) ?? 'app';
    const emitLevel = category === 'app' ? level : 'warn';
    logger.emit({
      severityText: emitLevel.toUpperCase(),
      body: maskTokens(args.map(serializeArg).join(' ')),
      attributes: {
        source,
        level: emitLevel,
        category,
        stackTrace: maskTokens(stack),
        ...(firstError?.stack ? { errorStack: maskTokens(firstError.stack) } : {}),
        location,
        errorType: firstError?.name ?? 'GeneralError',
        ...userAttributes,
      },
    });
  } catch {
    // Logging must never break the login page
  } finally {
    inLog = false;
  }
};

// Called once at startup, before the app renders, so a crash while the login page loads is reported too
export function initConsoleLogger(): void {
  if (logger) return;
  const loggerProvider = new LoggerProvider({ resource: getResource() });
  loggerProvider.addLogRecordProcessor(new BatchLogRecordProcessor(new OTLPLogExporter({ url: 'https://tracer.rupeezy.in/v1/logs' })));
  logger = loggerProvider.getLogger('browser-console-logger');

  (['warn', 'error'] as const).forEach((level) => {
    const original = console[level];
    const wrapper = (...args: unknown[]) => {
      original.apply(console, args);
      if (inLog) return;
      const stack = new Error().stack ?? '';
      // Drops our own wrapper frame, then finds who called console.*
      const caller = directCaller(stackFrames(stack).slice(1), console[level] !== wrapper);
      // Browser extensions log into our console too; those are not our errors
      if (EXTENSION_URL.test(frameUrl(caller))) return;
      emitLog(level, 'browser-console', args, stack, caller, isThirdPartyUrl(frameUrl(caller)));
    };
    console[level] = wrapper;
  });

  // Errors thrown outside console.* (e.g. in a click handler) never reach the wrapper above
  window.addEventListener('error', (event: ErrorEvent) => {
    if (EXTENSION_URL.test(event.filename ?? '')) return;
    const error = event.error instanceof Error ? event.error : undefined;
    const location = event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : 'unknown location';
    // No filename ("Script error.") means a cross-origin script the browser won't describe
    emitLog('error', 'window-error', [error ?? event.message], error?.stack ?? '', location, !event.filename || isThirdPartyUrl(event.filename));
  });
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const error = event.reason instanceof Error ? event.reason : undefined;
    const thrownAt = stackFrames(error?.stack)[0] ?? 'unknown location';
    if (EXTENSION_URL.test(frameUrl(thrownAt))) return;
    emitLog('error', 'unhandled-rejection', [error ?? event.reason], error?.stack ?? '', thrownAt, isThirdPartyUrl(frameUrl(thrownAt)));
  });

  // Logs are batched; send what is pending when the page is hidden or left (e.g. the redirect after login), or it is lost
  const flush = () => { void loggerProvider.forceFlush(); };
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}
